import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ErroApi } from '../api/client'
import {
  ACEITA_NO_SELETOR,
  modeloComArquivo as api,
  previa,
  type ModeloComArquivo,
  type ModelosDaConversa,
} from '../api/modeloComArquivo'
import { Botao } from '../componentes/Botao'
import { Estado } from '../componentes/Estado'
import { envioLembrado, esquecerEnvio, lembrarEnvio, type EnvioAcompanhado } from './envioAcompanhado'
import { StatusDoEnvioDoModelo } from './StatusDoEnvioDoModelo'
import '../embed/avaliacao.css'

/**
 * Enviar modelo de WhatsApp com arquivo — **tela do portal** (demanda 277).
 *
 * 🔴 **O caso:** fora da janela de 24 h só modelo aprovado sai, e o Chatwoot pede o
 * arquivo do cabeçalho como URL. O Guilherme, em 23/09: *"isto complica muito a
 * operação"*. Aqui o atendente escolhe o modelo, preenche o texto, anexa o arquivo do
 * computador e envia — a mensagem sai pelo Chatwoot e aparece na conversa.
 *
 * ⛔ **Fica no portal, e não no painel embutido** (decisão do Ferrari em 23/09, B2 da
 * revisão do PR #204). Enviar ao cliente exige o login do portal (2FA para quem o tem ativo); a sessão do
 * iframe nasce de um token de instalação e serve para ler. O painel só abre esta tela
 * numa aba, com a conversa na URL — e é por isso que o destinatário aparece em cima,
 * antes de qualquer campo.
 *
 * 🆕 **282 (28/09):** a mesma tela vive também na aba **"Enviar arquivo"** do Chatwoot
 * (`EnviarArquivoPainel`), que passa a conversa aberta por `conversa`. A regra do B2 não
 * muda: lá também só a sessão do PORTAL chega a este formulário.
 */
export function EnviarModelo({
  conversa: daConversaAberta,
  conferirAntesDeEnviar,
  botaoComNome = false,
}: {
  conversa?: number
  /**
   * 🔴 **282 (revisão):** no painel, a conversa aberta pode ter mudado entre escolher o
   * arquivo e clicar em enviar. Devolve o motivo da recusa, ou `null` para seguir. Roda
   * ANTES de qualquer chamada de envio.
   */
  conferirAntesDeEnviar?: () => Promise<string | null>
  /** O botão diz a quem vai ("Enviar a Nanderson") — no painel, ao lado de outra conversa. */
  botaoComNome?: boolean
} = {}) {
  const { t } = useTranslation()

  const conversa =
    daConversaAberta ?? (Number(new URLSearchParams(window.location.search).get('conversa')) || null)

  const [dados, setDados] = useState<ModelosDaConversa | null>()
  const [motivo, setMotivo] = useState<string | null>(null)
  const [escolhido, setEscolhido] = useState<ModeloComArquivo | null>(null)
  const [valores, setValores] = useState<Record<string, string>>({})
  const [botoes, setBotoes] = useState<(string | null)[]>([])
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [enviado, setEnviado] = useState<string | null>(null)

  // O último envio aceito nesta conversa, nesta sessão da aba: é dele a linha de estado.
  // Não some ao escolher o modelo seguinte — a falha da Meta chega depois, e é para ser vista.
  const [acompanhado, setAcompanhado] = useState<EnvioAcompanhado | null>(() =>
    conversa ? envioLembrado(conversa) : null,
  )

  useEffect(() => {
    if (!conversa) return

    api
      .listar(conversa)
      .then(setDados)
      .catch((e) => {
        // 404 (conversa desconhecida) e 422 (não é de WhatsApp) trazem o motivo do servidor.
        setMotivo(e instanceof ErroApi ? ((e.corpo as { detail?: string } | null)?.detail ?? null) : null)
        setDados(null)
      })
  }, [conversa])

  function escolher(chave: string) {
    const modelo = dados?.modelos.find((m) => `${m.nome}|${m.idioma}` === chave) ?? null

    setEscolhido(modelo)
    setValores({})
    setBotoes(modelo ? modelo.botoes.map(() => null) : [])
    setArquivo(null)
    setErro(null)
    setEnviado(null)
  }

  async function enviar() {
    if (!conversa || !escolhido || !arquivo) return

    setEnviando(true)
    setErro(null)

    if (conferirAntesDeEnviar) {
      const recusa = await conferirAntesDeEnviar()

      if (recusa) {
        setErro(recusa)
        setEnviando(false)
        return
      }
    }

    try {
      const resposta = await api.enviar(conversa, escolhido, arquivo, valores, botoes)

      // Sem o id (o Chatwoot aceitou e não disse qual mensagem), não há o que acompanhar.
      const novo =
        typeof resposta?.mensagemId === 'number'
          ? { nome: escolhido.nome, mensagemId: resposta.mensagemId, em: Date.now() }
          : null

      setAcompanhado(novo)
      if (novo) lembrarEnvio(conversa, novo)
      else esquecerEnvio(conversa)

      setEnviado(escolhido.nome)
      setEscolhido(null)
      setArquivo(null)
      setValores({})
    } catch (e) {
      // 🔴 **Só o servidor diz "nada foi enviado"** (revisão M-a). Resposta com o motivo dele —
      // recusa (4xx) ou a do próprio Chatwoot (502) — vale como está. Todo o resto — queda de
      // rede, 502/504 do proxy em HTML, JSON ilegível — é INCERTO: o envio pode ter saído.
      const corpo = e instanceof ErroApi ? (e.corpo as { detail?: string } | null) : null
      const doServidor = e instanceof ErroApi && (e.status < 500 || e.status === 502) ? corpo?.detail : null
      setErro(doServidor ?? t('modeloComArquivo.naoConfirmado'))
    } finally {
      setEnviando(false)
    }
  }

  if (!conversa) return <Estado tipo="vazio" titulo={t('modeloComArquivo.semConversa')} />

  if (dados === undefined) return <Estado tipo="carregando" />

  if (dados === null)
    return (
      <section className="avaliacao">
        <h1>{t('modeloComArquivo.titulo')}</h1>
        <p className="avaliacao__erro" role="alert">
          {motivo ?? t('modeloComArquivo.listaFalhou')}
        </p>
      </section>
    )

  const faltaAlgo =
    !escolhido ||
    !arquivo ||
    escolhido.variaveis.some((v) => !valores[v]?.trim()) ||
    escolhido.botoes.some((b, i) => b.pedeParametro && !botoes[i]?.trim())

  return (
    <section className="avaliacao">
      <h1>{t('modeloComArquivo.titulo')}</h1>

      {/* Quem recebe, em cima: esta aba está longe da conversa. */}
      <p className="avaliacao__trecho" data-testid="destinatario-do-modelo">
        {t('modeloComArquivo.destinatario', {
          nome: dados.conversa.contato ?? t('modeloComArquivo.semNome'),
          conversa: dados.conversa.idExterno,
          caixa: dados.conversa.caixa ?? '—',
        })}
      </p>

      <p className="avaliacao__dica">{t('modeloComArquivo.dica')}</p>

      {dados.modelos.length === 0 && <p className="contexto__vazio">{t('modeloComArquivo.nenhum')}</p>}

      {dados.modelos.length > 0 && (
        <div className="avaliacao__caixa">
          <label className="avaliacao__rotulo" htmlFor="modelo-com-arquivo">
            {t('modeloComArquivo.modelo')}
          </label>
          <select
            id="modelo-com-arquivo"
            className="avaliacao__campo"
            value={escolhido ? `${escolhido.nome}|${escolhido.idioma}` : ''}
            onChange={(e) => escolher(e.target.value)}
          >
            <option value="">{t('modeloComArquivo.escolha')}</option>
            {dados.modelos.map((m) => (
              <option key={`${m.nome}|${m.idioma}`} value={`${m.nome}|${m.idioma}`}>
                {m.nome} · {m.aceita}
              </option>
            ))}
          </select>

          {escolhido && (
            <>
              {escolhido.variaveis.map((v) => (
                <div key={v}>
                  <label className="avaliacao__rotulo" htmlFor={`variavel-${v}`}>
                    {t('modeloComArquivo.variavel', { nome: v })}
                  </label>
                  <input
                    id={`variavel-${v}`}
                    className="avaliacao__campo"
                    value={valores[v] ?? ''}
                    maxLength={1000}
                    onChange={(e) => setValores({ ...valores, [v]: e.target.value })}
                  />
                </div>
              ))}

              {escolhido.botoes.map((b, i) =>
                b.pedeParametro ? (
                  <div key={`botao-${i}`}>
                    <label className="avaliacao__rotulo" htmlFor={`botao-${i}`}>
                      {t('modeloComArquivo.botao', { texto: b.texto })}
                    </label>
                    <input
                      id={`botao-${i}`}
                      className="avaliacao__campo"
                      value={botoes[i] ?? ''}
                      onChange={(e) => setBotoes(botoes.map((x, j) => (j === i ? e.target.value : x)))}
                    />
                  </div>
                ) : null,
              )}

              {/* Da prévia ao botão em letra maior (pedido do Guilherme, 30/09):
                  é o que o atendente confere antes de enviar. */}
              <div className="enviar-modelo__final">
                <p className="avaliacao__rotulo">{t('modeloComArquivo.previa')}</p>
                <p className="avaliacao__trecho" data-testid="previa-do-modelo">
                  {previa(escolhido.corpo, valores)}
                </p>

                <label className="avaliacao__rotulo" htmlFor="arquivo-do-modelo">
                  {t('modeloComArquivo.arquivo', { aceita: escolhido.aceita, teto: escolhido.tetoEmMb })}
                </label>
                <input
                  id="arquivo-do-modelo"
                  type="file"
                  accept={ACEITA_NO_SELETOR[escolhido.formato]}
                  onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
                />

                <div className="avaliacao__acoes">
                  <Botao onClick={enviar} disabled={enviando || faltaAlgo}>
                    {enviando
                      ? t('modeloComArquivo.enviando')
                      : botaoComNome
                        ? t('modeloComArquivo.enviarA', {
                            nome: dados.conversa.contato ?? t('modeloComArquivo.semNome'),
                          })
                        : t('modeloComArquivo.enviar')}
                  </Botao>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {erro && (
        <p className="avaliacao__erro" role="alert">
          {erro}
        </p>
      )}
      {enviado && <p className="avaliacao__dica">{t('modeloComArquivo.enviado', { nome: enviado })}</p>}
      {acompanhado && (
        <StatusDoEnvioDoModelo key={acompanhado.mensagemId} conversa={conversa} envio={acompanhado} />
      )}
    </section>
  )
}
